/**
 * D-033: pick a single envy-signal line for a dormant CarCard from the
 * per-model rollup returned by `get_user_dormant_signals` RPC.
 *
 * Priority order (only one signal renders per card to avoid spam):
 *   1. sold_count_7d >= 1       → "N sold this week · Pro users knew within 24h"
 *   2. price_drop_count_7d >= 1 → "N price drops this week · locked to Pro"
 *   3. lowest_new_match_price   → "New match under £Xk · upgrade to see"
 *
 * Returns `null` when no signal fires; the card then falls back to the
 * existing "Activate with Pro" label per D-015.
 *
 * **Note on price drops:** prod signal density is currently 0% because the
 * listings worker's regen flow never re-surfaces the same (user, model,
 * marketcheck_id) across batches, so `priorPriceMap` in
 * `generateListingFeedHandler.js` is a cold-miss and the Step 21 write never
 * fires. Tracked at https://github.com/lynxul25/aidoocarsparc/issues/4.
 * Once that closes, this tier starts rendering automatically without a
 * frontend change.
 *
 * **Sold threshold is `>= 1`** — on prod data ~22% of models hit `>= 2` but
 * ~50%+ hit `>= 1`, and "1 sold this week · Pro users knew within 24h" still
 * reads strong. Copy stays plural-safe ("1 sold" vs "3 sold") via the bare
 * "N sold" phrasing without a plural suffix on "sold".
 */

const roundToK = (price) => {
  if (price == null || Number.isNaN(price)) return null;
  if (price < 1000) return null;
  return Math.floor(price / 1000);
};

export function selectDormantSignalLine(signals) {
  if (!signals) return null;
  const sold = Number(signals.sold_count_7d || 0);
  const drops = Number(signals.price_drop_count_7d || 0);
  const lowest = signals.lowest_new_match_price;

  if (sold >= 1) {
    return `${sold} sold this week · Pro users knew within 24h`;
  }
  if (drops >= 1) {
    return `${drops} price drop${drops === 1 ? '' : 's'} this week · locked to Pro`;
  }
  const k = roundToK(lowest);
  if (k != null) {
    return `New match under £${k}k · upgrade to see`;
  }
  return null;
}
